import { useAppSelector } from '@/shared/store'
import { GridColDef } from '@mui/x-data-grid-premium'

import { selectColumnDefinitionsByGridType } from '../columnDefinitionsSelector'
import { commonGridColDefs } from '../commonColDefs'
import { ColumnDefinition } from '../models/ColumnDefinition'
import { GridTypeName } from '../models/GridTypeName'

/**
 * Retrieves a nested value from an object using a dot-separated path.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current !== null && current !== undefined ? current[key] : undefined
  }, obj)
}

/*
 * Retrieves a single column definitions used by data grid table.
 * Note:
 *   - The columnNames are predefined and stored on the server's database.
 *   - For custom column definitions, users can define the colums directly
 *      in the component code.
 * @param columnName  The column name as defined by the server.
 * @param field       Optional. The field in the data source that this column will display.
 * @param customDef   Optional. Additional custom settings to override or extend the 
 *                      predefined column settings in commonColDefs.
 * @returns           Returns a GridColDef object containing the complete column definition,
 *                      or null if the columnName does not exist in the predefined list.
 */

export const getColDef = (columnName: ColumnDefinition['name'], field?: GridColDef['field'], customDef?: Partial<GridColDef>): GridColDef | null => {
  if (['dragDrop', 'orderDetails', 'orderDetailsCollapsed', 'addressDetails',
    'customerDetails', 'depotDetails', 'runningTotals', 'eta'].includes(columnName)) {
    return null
  }

  if (!commonGridColDefs[columnName]) {
    // console.warn('Attempted to get a column definition for an unknown column name: ', columnName)

    return null
  }

  const colDef: GridColDef = {
    ...commonGridColDefs[columnName],
    ...(field && {
      field: field,
      valueGetter: (_, row) => {
        return getNestedValue(row, field)
      },
    }),
    ...customDef,
  }

  return colDef
}

/*
 * Retrieves the column definitions used by data grid table based on the data grid type.
 * @param gridTypeName  The grid type name of the grid
 * @param override      Optional. A map of column names and their custom settings to override or extend the
 * 
 * @returns           Returns a GridColDef object containing the complete column definition,
 *                      or null if the columnName does not exist in the predefined list.
 */
export type GetGridColDefsPayload = {
  gridTypeName: GridTypeName
  override?: Record<string, Partial<GridColDef>>
  additionalColDefs?: GridColDef[]
}

export const getGridColDefs = ({
  gridTypeName,
  override,
  additionalColDefs = [],
}: GetGridColDefsPayload) => {
  // Retrieve pre-defined column definitions that has been fetched from the server
  const dbColDefs = useAppSelector(state => selectColumnDefinitionsByGridType(state, gridTypeName))

  if (!dbColDefs || dbColDefs.length == 0) {
    return []
  }

  const gridColDefs: GridColDef[] = []

  for (let i = 0; i < dbColDefs.length; i++) {
    const dbColDef = dbColDefs[i]

    if (dbColDef.isGroup) {
      continue
    }
    
    const colDef = getColDef(dbColDef.name, undefined, override?.[dbColDef.name])

    if (colDef)
      gridColDefs.push(colDef)
  }

  gridColDefs.push(...additionalColDefs)

  return gridColDefs
}

